import React, { useState, useEffect } from 'react';
import { IntelligenceLayout, fetchWithAuth } from './IntelligenceLayout';
import { BookOpen, Zap, Target, Users, Mail, BarChart3, Send, ShieldCheck, Filter, Brain, Rocket, ChevronRight, CheckCircle, AlertTriangle, Clock, ArrowRight, Settings, Repeat, Truck, Layers, Eye, Shield, Building2, Compass, Monitor } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ───────────────────────────────────────────────────────────────────────
   QUICK START — "Get Started in 30 Minutes"
   ─────────────────────────────────────────────────────────────────────── */

const DAILY_BENCHMARKS = [
  { metric: "2-3", label: "strong roles identified" },
  { metric: "1-2", label: "candidates per role pitched" },
  { metric: "5-10", label: "emails sent per day" },
  { metric: "1-3", label: "replies received" },
];

const QUICK_START_STEPS = [
  {
    step: 1,
    title: "Open Smart Recommendations",
    desc: "Go to Acquisition Pipeline or check your morning digest email. The system surfaces 3-5 top roles daily based on pipeline strength, candidate quality, and viability score.",
    action: "Open Acquisition Pipeline tab",
    icon: Brain,
    bullets: [
      "Only focus on roles tagged High Viability (green) or Strong Pipeline",
      "Ignore low viability, defense/clearance, or empty pipeline roles",
      "The system already filters these — trust the recommendations",
    ],
  },
  {
    step: 2,
    title: "Choose the Top Role",
    desc: "Expand a recommended role in Candidate Pipelines. Pick strategically — not every role is worth your time.",
    action: "Open Candidate Pipelines tab",
    icon: Target,
    bulletLabel: "Choose a role that has:",
    bullets: [
      "High Viability tag (green badge)",
      "2 or more strong-fit candidates (score 50+)",
      "Recent pipeline activity or improvement",
    ],
  },
  {
    step: 3,
    title: "Only Act on Candidates Scoring 50+ — Skip Everything Else",
    desc: "Use Compare to see AI-powered side-by-side analysis. Only act on candidates scoring 50 or above — they are your strongest leads. If no one hits 50, skip this role entirely.",
    action: "Compare top 2 candidates",
    icon: Users,
    bullets: [
      "Score 50+ = act immediately — these are your targets",
      "Score 30-49 = skip unless pipeline is thin and role is urgent",
      "Score below 30 = never pursue — move to the next role",
      "Check for title match, relevant industry, and location fit",
    ],
  },
  {
    step: 4,
    title: "Generate Pitch (Position as Immediate Value)",
    desc: "Click 'Generate Client Pitch'. Choose Short (cold outreach) or Detailed (warm leads). Always edit to add personal context before sending.",
    action: "Click Generate Client Pitch button",
    icon: Zap,
    bullets: [
      "Short pitch = 3-4 sentences, ideal for first touch",
      "Detailed pitch = full candidate brief, ideal for warm contacts",
      "Edit the tone — the AI writes consultatively, but add your personal touch",
    ],
  },
  {
    step: 5,
    title: "Send Email (Use Scheduling)",
    desc: "Click 'Send via Email'. Fill in the recipient, review the message, then choose your send timing.",
    action: "Send or schedule the pitch",
    icon: Send,
    bullets: [
      "Start with 5-10 emails per day max (protect domain reputation)",
      "Use Smart Schedule to auto-send at the next business hour",
      "Pick Time for precise control (business hours 8 AM - 5 PM only)",
      "Send Now shows a warning if outside business hours",
    ],
  },
  {
    step: 6,
    title: "Track Responses in Submissions",
    desc: "Log each pitch via 'Submit to Client'. Track status in the Submissions tab. The system auto-creates follow-up sequences.",
    action: "Log and track in Submissions tab",
    icon: BarChart3,
    bullets: [
      "Update status immediately when replies come in",
      "Follow up if no response within 2-3 business days",
      "Check Email Queue > Needs Attention for overdue follow-ups",
      "Mark as Replied when you get a response — this cancels auto follow-ups",
    ],
  },
  {
    step: 7,
    title: "Follow Up (If No Reply in 2-3 Days)",
    desc: "The system auto-generates a follow-up and queues it for your approval. Review it, approve, and send. Keep it light — do NOT resend the original pitch.",
    action: "Check Email Queue > Follow-Ups tab",
    icon: Repeat,
    bullets: [
      "Follow-ups appear in Email Queue > Follow-Ups tab as 'Awaiting Approval'",
      "Review the AI-generated message — edit if needed, then Approve & Schedule",
      "Keep follow-ups short (2-3 sentences) — a light nudge, not a re-pitch",
      "If they reply, all pending follow-ups are auto-cancelled",
      "Most deals happen on the 2nd or 3rd touch — don't skip this step",
    ],
  },
];

const WHAT_TO_IGNORE = [
  { label: "Low Viability roles", desc: "Red 'Low - Hard to Fill' tag. Auto-filtered from main view." },
  { label: "Defense/Clearance roles", desc: "Lockheed Martin, Raytheon, etc. Auto-excluded. Clearance-required roles are deprioritized." },
  { label: "Empty pipelines", desc: "Roles with 0 candidates. Skip or wait for next sourcing cycle." },
  { label: "Weak candidate pools", desc: "No candidates scoring 50+. Your time is better spent on stronger pipelines." },
];

const GOOD_PIPELINE_SIGNS = [
  "2+ strong-fit candidates (score 50 or above)",
  "Average candidate score 40 or above",
  "'Strong Pipeline' or 'High Viability' tags",
  "Common, fillable job titles (Analyst, Specialist, Coordinator)",
  "Mid-level seniority (largest talent pool)",
];

const SAFE_SENDING = [
  "5-10 emails/day maximum (early stage — increase gradually)",
  "Always use Smart Schedule or Pick Time (avoid nights/weekends)",
  "Short pitches for cold outreach, detailed for warm leads",
  "Always preview and edit before sending — never send blind",
  "Monitor reply rates in Email Queue — adjust approach based on data",
  "Follow-ups auto-generate but always require your approval before sending",
];

const ACT_VS_SKIP = [
  { type: "act", label: "ACT", items: [
    "High viability role + 2+ candidates scoring 50+",
    "Role with recent pipeline improvement",
    "Warm contact at the company",
    "Follow-up due on a previously sent pitch",
  ]},
  { type: "skip", label: "SKIP", items: [
    "Low viability or defense/clearance role",
    "No candidates scoring above 50",
    "Pipeline with 0 candidates after sourcing",
    "Role where you already have 3+ unanswered pitches",
  ]},
];

/* ───────────────────────────────────────────────────────────────────────
   TRAINING MODULES — 9 comprehensive modules
   ─────────────────────────────────────────────────────────────────────── */

const MODULES = [
  {
    id: "smart-recs",
    title: "Smart Recommendations (Daily Digest)",
    icon: Brain,
    color: "text-purple-600 bg-purple-50",
    sections: [
      { heading: "What it does", content: "Every morning, the system evaluates all your roles and selects the top 3-5 most actionable opportunities based on pipeline strength, candidate quality, and role viability. Low viability, defense, clearance, and empty pipeline roles are always excluded." },
      { heading: "When to use it", content: "This is your daily starting point. Open the digest email or check the Acquisition Pipeline each morning. Don't browse all roles — let the system tell you where to focus." },
      { heading: "How it works (step by step)", content: "1. System scores all roles by: candidate count, average fit score, and viability. 2. Roles qualify if they have 2+ strong-fit candidates, average score 35+, or viability 60+. 3. Top 5 are selected and ranked. 4. Digest email arrives at your configured time with action links." },
      { heading: "Configure your digest", content: "Go to Training Portal > Digest Settings. Enable email delivery, set timezone (delivery at 7:30 AM local), add recipient emails. You can also click 'Send Preview' to test immediately." },
      { heading: "What good looks like", content: "2-3 recommended roles per day with High Viability tags. If you see 0 recommendations, it means no roles currently meet the quality threshold — this is by design, not a bug." },
    ],
  },
  {
    id: "adaptive-targeting",
    title: "Adaptive Targeting (2-Pass Sourcing)",
    icon: Target,
    color: "text-indigo-600 bg-indigo-50",
    sections: [
      { heading: "What it does", content: "When initial candidate sourcing produces a weak pipeline (few candidates or low scores), the system automatically expands search criteria and re-sources with broader parameters. This is the 2-pass sourcing system." },
      { heading: "When it activates", content: "Automatically, after the first sourcing pass. If a role has fewer than 3 candidates or average score below 30, the system triggers a second pass with relaxed criteria (wider location, broader titles, adjacent industries)." },
      { heading: "How to use it", content: "You don't manually trigger this — it happens behind the scenes. When you see a role with 'Adaptive Sourcing' tag, it means the system expanded the search. Review the results and compare with the original criteria to understand what was relaxed." },
      { heading: "Targeting Playbooks", content: "The system uses playbooks to define sourcing strategy: Standard (balanced), Aggressive (wider search, flexible titles), Precision (exact match only). The playbook is auto-selected based on role characteristics, but you can override." },
      { heading: "What good looks like", content: "After adaptive targeting, you should see 5+ candidates with at least 2 scoring 50+. If the pipeline is still weak after 2 passes, the role may genuinely be hard to fill — consider adjusting the role criteria or skipping." },
    ],
  },
  {
    id: "role-filtering",
    title: "Role Viability & Filtering",
    icon: Filter,
    color: "text-amber-600 bg-amber-50",
    sections: [
      { heading: "What it does", content: "Each role gets a viability score (0-100) based on title commonality, seniority level, company status, and clearance requirements. The system auto-filters low-quality roles so you focus on what's fillable." },
      { heading: "Score breakdown", content: "Score 60+ = High Viability (green tag, shown first). Score 30-59 = Moderate (amber tag). Score below 30 = Low - Hard to Fill (red tag, auto-filtered from main view). Defense contractors and clearance-required roles receive heavy penalties." },
      { heading: "Excluded companies", content: "These are permanently excluded from the main pipeline: Lockheed Martin, Raytheon, Northrop Grumman, L3Harris, General Dynamics, BAE Systems, Leidos. They appear in the 'Filtered / Low Priority' section only." },
      { heading: "Focus My Time mode", content: "Toggle 'Focus My Time' in Acquisition Pipeline to completely hide low viability, excluded, and clearance roles. Shows only your best opportunities sorted by composite value score." },
      { heading: "How to use it", content: "Trust the system's filtering. Focus on green (High Viability) roles first, then amber (Moderate) if you have capacity. Red (Low) roles are rarely worth pursuing unless you have a specific reason." },
    ],
  },
  {
    id: "candidate-tools",
    title: "Candidate Scoring & Comparison",
    icon: Users,
    color: "text-emerald-600 bg-emerald-50",
    sections: [
      { heading: "What it does", content: "Each sourced candidate gets an AI-powered fit score (0-100) based on title match, industry alignment, skills, location, and experience. The comparison view lets you evaluate candidates side-by-side before pitching." },
      { heading: "Score ranges", content: "Score 50+ = High Fit (green border) — your primary targets. Score 30-49 = Medium Fit — consider if pipeline is thin. Score below 30 = Low Fit — generally skip unless the role is niche." },
      { heading: "How to compare", content: "1. In Candidate Pipelines, expand a role. 2. Select 2-3 candidates using checkboxes. 3. Click 'Compare'. 4. AI generates a side-by-side analysis with strengths, weaknesses, and a recommendation. 5. Use this before pitching to pick the best 1-2 candidates." },
      { heading: "When to use it", content: "Always compare before generating a pitch, especially when you have 3+ strong-fit candidates. The comparison helps you pick the strongest pairing to present to the client." },
      { heading: "What good looks like", content: "A comparison where at least 1 candidate is clearly strong (score 60+) with relevant experience. If all candidates are mediocre (30-45), consider waiting for the next sourcing cycle." },
    ],
  },
  {
    id: "pitch-workflow",
    title: "Pipeline to Pitch to Send Workflow",
    icon: Rocket,
    color: "text-blue-600 bg-blue-50",
    sections: [
      { heading: "What it does", content: "The complete outreach workflow: identify a strong role, select candidates, generate an AI pitch, send or schedule the email, and track the outcome. This is the core revenue-generating activity." },
      { heading: "Step 1: Generate pitch", content: "On any role with candidates, click 'Generate Client Pitch'. Choose Short (cold outreach, 3-4 sentences) or Detailed (warm leads, full candidate brief). The AI writes in a consultative tone — not recruiter-speak." },
      { heading: "Step 2: Edit and personalize", content: "Always review and edit. Add context: why these candidates matter to this specific recipient, reference any previous conversations, mention relevant industry trends." },
      { heading: "Step 3: Send or schedule", content: "Click 'Send via Email'. Three options: Send Now (immediate, warns if outside business hours), Smart Schedule (auto-sends at next business hour), Pick Time (you choose date/time, 8 AM-5 PM only)." },
      { heading: "Step 4: Log submission", content: "After sending, click 'Submit to Client' to log the submission. The system auto-links the pitch email and creates a follow-up sequence. Track outcomes in the Submissions tab." },
      { heading: "What good looks like", content: "A personalized pitch sent during business hours to a decision-maker, with 1-2 strong candidates (score 50+), resulting in a reply within 2-3 days." },
    ],
  },
  {
    id: "email-queue",
    title: "Email Queue & Delivery Tracking",
    icon: Truck,
    color: "text-cyan-600 bg-cyan-50",
    sections: [
      { heading: "What it does", content: "The Email Queue (sidebar: CRM & Outreach > Email Queue) is your operational control panel. See every pitch email's status: Pending, Sent, Delivered, Replied, or Failed. Track delivery reliability and reply rates in real-time." },
      { heading: "Needs Attention tab", content: "This is your action list. It aggregates: failed emails (need retry or reschedule) and sent emails with no reply after 2+ days (need follow-up). Check this daily — it tells you exactly what to do next." },
      { heading: "Follow-Up Due indicator", content: "Emails sent 2+ days ago without a reply show a pulsing orange 'Follow-up due' badge. Most deals happen on the 2nd or 3rd touch — don't ignore these." },
      { heading: "Managing emails", content: "Pending: Edit time or Cancel. Failed: Retry (send now) or Reschedule. Sent/Delivered: Mark as Replied (with notes). Click any email row to expand details (schedule ID, role, timezone, failure reason)." },
      { heading: "Reply tracking", content: "When a recipient replies, click 'Mark as Replied' on that email. Add notes about what they said. This is the primary success signal — Reply Rate is shown as the key metric." },
      { heading: "Delivery stats", content: "The dashboard shows 7 metrics: Action Needed, Pending, Sent, Delivered, Replied, Failed, Reply Rate. Replied and Reply Rate are highlighted — these are what matter." },
    ],
  },
  {
    id: "follow-ups",
    title: "Follow-Up Sequences",
    icon: Repeat,
    color: "text-teal-600 bg-teal-50",
    sections: [
      { heading: "What it does", content: "When you send a pitch email, the system auto-generates a follow-up message and schedules it for Day 3. The follow-up is short, warm, and doesn't repeat the original pitch. Most replies happen on follow-ups — this is critical for conversion." },
      { heading: "Approval required", content: "Follow-ups are auto-generated and auto-scheduled, but they require your approval before sending. You always have control. Check the Email Queue > Follow-Ups tab to see pending approvals." },
      { heading: "How to manage", content: "1. Go to Email Queue > Follow-Ups tab. 2. Expand a sequence to see the message preview. 3. Click 'Approve & Schedule' to send it, or 'Edit' to modify the message first. 4. Click 'Regenerate AI' to get a fresh message. 5. Click 'Cancel Sequence' to stop all follow-ups for that recipient." },
      { heading: "Auto-cancel on reply", content: "When you mark a pitch email as Replied, all pending follow-ups for that recipient are automatically cancelled. No risk of sending a follow-up after someone already responded." },
      { heading: "Daily limits apply", content: "Follow-ups count toward your daily send limit (10/day). The system checks the limit before allowing approval. If you're at the limit, you'll need to wait until the next day." },
      { heading: "Message style", content: "Follow-up #1 (Day 3): Light nudge referencing the original email. Example tone: 'Just wanted to bump this - happy to share details if helpful.' Never aggressive, never repeating the original pitch." },
    ],
  },
  {
    id: "submission-tracking",
    title: "Submission Tracker & Analytics",
    icon: BarChart3,
    color: "text-[#D4AF37] bg-amber-50",
    sections: [
      { heading: "What it does", content: "The Submissions tab tracks every client submission from pitch to placement. See the full conversion funnel, track individual candidate progress, and measure what's working." },
      { heading: "Status tracking", content: "Each submission moves through: Submitted > Pitch Sent > Interviewing > Offer > Placed. Filter by status, search by company or role. Status updates are logged with timestamps." },
      { heading: "Conversion funnel", content: "The analytics view shows your pipeline funnel: total submissions, how many moved to interviews, offers, and placements. Breakdown by role type and playbook helps you see which strategies work best." },
      { heading: "Pitch email analytics", content: "Reply-focused analytics: track reply rate per pitch, compare short vs detailed pitches, see performance by candidate score range. The system highlights insights like 'Candidates scoring 50+ get 2x reply rate' to improve your approach." },
      { heading: "How to use it", content: "Check Submissions weekly. Look for: which role types get the most replies, which pitch style performs better, and which candidate score range converts. Adjust your strategy based on data, not gut feeling." },
    ],
  },
  {
    id: "outreach-safety",
    title: "Outreach Safety & Controls",
    icon: ShieldCheck,
    color: "text-red-600 bg-red-50",
    sections: [
      { heading: "Why this matters", content: "Bad outreach = spam folder. Protecting your sender reputation is non-negotiable. The system has multiple safeguards built in, but you must also follow best practices." },
      { heading: "Daily send limits", content: "10 emails/day per inbox. This includes both direct sends and approved follow-ups. Start with 5-10/day during early stage. The limit is visible in the send modal and enforced system-wide." },
      { heading: "Business hours only", content: "Smart Schedule ensures emails arrive 8 AM - 5 PM in the recipient's timezone. Sending outside hours triggers a warning. Weekend sends are auto-pushed to Monday. Never override this." },
      { heading: "Duplicate prevention", content: "The system blocks duplicate sends: same recipient + same role + same day = blocked. This prevents accidental double-sends from the queue or follow-up system." },
      { heading: "Campaign activation safety", content: "Before activating any Instantly.ai outreach campaign, a confirmation modal shows: total contacts, sending account, daily limits, and sequence preview. Review every detail before confirming." },
      { heading: "Domain warmup", content: "New domains need gradual volume. Week 1: 5 emails/day. Week 2: 10/day. Week 3: 15/day. Monitor bounce rates — if above 3%, pause and investigate." },
      { heading: "Emergency controls", content: "Cancel any scheduled email from the Email Queue. Cancel entire follow-up sequences with one click. All actions are logged for audit." },
    ],
  },
];

/* ───────────────────────────────────────────────────────────────────────
   INTELLIGENCE GUIDE — Current System Operating Reference
   ─────────────────────────────────────────────────────────────────────── */

const TIER_DEFINITIONS = [
  {
    tier: "Best Immediate",
    color: "bg-emerald-50 border-emerald-300 text-emerald-800",
    badge: "bg-emerald-100 text-emerald-700",
    what: "Confirmed mid-market (50-1,000 employees), strong hiring signals, verified firmographic data, high outreach fit. These are the companies most likely to need and pay for your recruiting services right now.",
    action: "Work these FIRST every day. These are your highest-probability targets. Move straight to outreach.",
    signals: ["Verified employee count in range", "ICP industry match or strong niche score", "Active hiring (multiple postings)", "High firmographic confidence"],
  },
  {
    tier: "Broader Qualified",
    color: "bg-gray-50 border-gray-300 text-gray-700",
    badge: "bg-gray-100 text-gray-600",
    what: "Passed all qualification checks but ranked lower than Best Immediate. Typically: right size but weaker hiring signals, non-ICP industry with moderate fit, or growth-stage companies without repeated hiring patterns.",
    action: "Use for more volume once you have worked through Best Immediate. Good second-pass targets.",
    signals: ["Qualified but fewer urgency signals", "Non-ICP industry with decent activity", "Growth stage without repeated titles"],
  },
  {
    tier: "Needs Enrichment",
    color: "bg-amber-50 border-amber-300 text-amber-800",
    badge: "bg-amber-100 text-amber-700",
    what: "Missing or unreliable firmographic data. Employee count unknown, industry unclear, or data mismatch detected (e.g., 10 open roles but listed as 5 employees). Could be great targets once data is verified.",
    action: "Use selectively. Quick-verify the company before outreach — check their website or LinkedIn for size/industry. Use Enrichment Overrides to correct bad data.",
    signals: ["Employee count unknown or suspect", "Industry missing or inferred", "Low firmographic confidence flag", "Data mismatch detected"],
  },
  {
    tier: "Strategic Enterprise",
    color: "bg-indigo-50 border-indigo-300 text-indigo-800",
    badge: "bg-indigo-100 text-indigo-700",
    what: "Large enterprises (1,000+ employees) that are real companies with real hiring needs — but harder to access. They typically use internal TA teams or enterprise RPOs. NOT the same as excluded junk.",
    action: "Hidden by default. Use intentionally when you want to pursue a long-term, high-value account. Requires a different approach: target specific divisions, not the whole company.",
    signals: ["Enterprise-class headcount", "Strong hiring volume", "Worth tracking for relationship-building", "Different sales motion than mid-market"],
  },
];

const BROAD_VS_NICHE = [
  {
    title: "All Qualified Targets",
    subtitle: "Broad discovery across every targetable industry",
    icon: Compass,
    when: "When you want maximum volume and variety. Surfaces every company that passed qualification regardless of industry lane.",
    tip: "Best for: exploring new verticals, finding unexpected opportunities, and filling your pipeline broadly.",
  },
  {
    title: "4 Specialty Lanes",
    subtitle: "Accounting & Finance, Healthcare, Legal, Logistics",
    icon: Target,
    when: "When you want to focus on industries where you have deep expertise. These are focused filters, not the only industries you can work — every qualified company appears in the broad view regardless of lane.",
    tip: "Best for: leveraging niche expertise, higher-confidence outreach, faster conversations with industry-specific knowledge.",
  },
];

const CONFIDENCE_SIGNALS = [
  {
    level: "High Confidence",
    color: "text-emerald-700 bg-emerald-50",
    meaning: "Employee count verified, industry confirmed, data consistent. Trust the classification — safe to contact without additional research.",
    flags: [],
  },
  {
    level: "Medium Confidence",
    color: "text-amber-700 bg-amber-50",
    meaning: "Some data verified but gaps remain. Industry might be inferred from job titles rather than confirmed. Employee count from a secondary source.",
    flags: ["Industry inferred, not confirmed", "Employee count from job posting volume estimate"],
  },
  {
    level: "Low Confidence",
    color: "text-red-700 bg-red-50",
    meaning: "Data is suspect or missing. Common flags: role count exceeds employee count, suspiciously low headcount for posting volume. Auto-downranked from Best Immediate to Needs Enrichment.",
    flags: ["roles > employees mismatch", "Headcount suspiciously low", "No industry data available", "Employee count unknown"],
  },
];

const DAILY_FLOW_STEPS = [
  { step: "1", label: "Start with Best Immediate", desc: "Open your intelligence surfaces. Filter to Best Immediate tier. These are your highest-probability targets today.", color: "bg-emerald-500" },
  { step: "2", label: "Work Broader Qualified for volume", desc: "After working through Best Immediate, expand to Broader Qualified for additional outreach targets.", color: "bg-gray-500" },
  { step: "3", label: "Use Needs Enrichment selectively", desc: "Spot-check interesting companies in Needs Enrichment. Quick-verify size/industry before reaching out.", color: "bg-amber-500" },
  { step: "4", label: "Use Strategic Enterprise intentionally", desc: "Toggle on Strategic Enterprise only when you want to pursue a specific large account. Different playbook needed.", color: "bg-indigo-500" },
  { step: "5", label: "Move into outreach from strongest first", desc: "Start outreach from your most confident targets. Best Immediate with high confidence = highest priority sends.", color: "bg-[#D4AF37]" },
];

const SCREEN_GUIDES = [
  {
    screen: "Daily Command Center",
    path: "Intelligence > Daily Command Center",
    icon: Monitor,
    purpose: "Your morning briefing. Shows today's top opportunities, surges, and pipeline health at a glance.",
    howToUse: "Check this first each morning. The tier-segmented KPI cards show how many Best Immediate, Needs Enrichment, and Broader Qualified opportunities are active. Click any card to drill into that tier. Focus on the Best Immediate count — that is your action number for the day.",
    keyFeatures: ["Tier-segmented KPI cards", "Top opportunities ranked by outreach fit", "Hiring surge alerts", "Pipeline snapshot"],
  },
  {
    screen: "Target Acquisition (Top Companies)",
    path: "Intelligence > Top Hiring Companies",
    icon: Target,
    purpose: "Your primary target list. Companies ranked by target score and outreach fit — not just hiring volume.",
    howToUse: "This is sorted by target_score (how valuable they are as a prospect), not raw job count. Use the tier filter bar to focus on Best Immediate first. The outreach fit score tells you how well each company matches your services. Click any company to see full details and AI classification.",
    keyFeatures: ["Sorted by target score, not volume", "Outreach fit scoring", "Tier filter bar", "AI ambiguity classification in detail modal"],
  },
  {
    screen: "Client Opportunities",
    path: "Intelligence > Client Opportunities",
    icon: Building2,
    purpose: "Companies with the strongest client-conversion signals — multi-role hiring, hard-to-fill positions, hiring surges.",
    howToUse: "Use tier filters to focus on Best Immediate. Companies here have scoring signals that indicate they are struggling to hire and may welcome recruiting help. Pair with the Hiring Surge detector for time-sensitive opportunities.",
    keyFeatures: ["Opportunity scoring signals", "Tier badges + confidence flags", "Outreach fit column"],
  },
  {
    screen: "Hiring Scanner",
    path: "Intelligence > Hiring Scanner",
    icon: Eye,
    purpose: "Real-time feed of fresh job postings. See what is being posted right now across your target market.",
    howToUse: "Use to spot newly posted roles and identify companies entering active hiring mode. The tier badge on each entry tells you if the company is already qualified as a target. Focus on entries from Best Immediate companies.",
    keyFeatures: ["Real-time posting feed", "Tier classification per company", "Confidence indicators"],
  },
  {
    screen: "Hiring Surge Detector",
    path: "Intelligence > Surge Detector",
    icon: Zap,
    purpose: "Companies with sudden spikes in job posting velocity. These are time-sensitive outreach opportunities.",
    howToUse: "Surges mean a company is ramping up hiring faster than normal — they may be overwhelmed. Filter by Best Immediate to find surge companies that are also strong ICP matches. These are your 'act now' targets.",
    keyFeatures: ["Velocity-based surge detection", "Tier filter bar", "Time-sensitivity indicator"],
  },
  {
    screen: "Hiring Pressure",
    path: "Intelligence > Hiring Pressure",
    icon: Layers,
    purpose: "Companies under sustained hiring pressure — long-open roles, high volume relative to company size.",
    howToUse: "Unlike surges (sudden spikes), pressure is sustained difficulty. Companies with high hiring pressure and Best Immediate tier are ideal for consultative outreach — they have been struggling to fill roles and may be ready for help.",
    keyFeatures: ["Sustained pressure scoring", "Tier classification", "Pressure vs company size ratio"],
  },
  {
    screen: "Niche Lanes",
    path: "Acquisition Pipeline > Niche Lanes",
    icon: Filter,
    purpose: "Focused views into your 4 specialty verticals: Accounting & Finance, Healthcare, Legal, Logistics.",
    howToUse: "Use when you want to leverage industry-specific expertise. Each lane shows companies with strong niche scoring in that vertical. The broad All Qualified view shows everything — lanes are filters, not boundaries.",
    keyFeatures: ["4 specialty lane filters", "Niche score per company", "Role matching within lane", "Similar company discovery"],
  },
  {
    screen: "Weekly Report",
    path: "Intelligence > Weekly Report",
    icon: BarChart3,
    purpose: "Automated weekly summary of new opportunities, surges, and market movements.",
    howToUse: "Review every Monday. The tier summary shows how many Best Immediate targets were identified that week. Use this to spot trends and adjust your targeting strategy.",
    keyFeatures: ["Tier-aware summary", "Week-over-week comparisons", "Top opportunities of the week"],
  },
];

const IntelligenceGuide = () => {
  const [expandedSection, setExpandedSection] = useState('tiers');

  const sections = [
    { id: 'tiers', label: 'Tier Logic', icon: Layers },
    { id: 'views', label: 'Broad vs Niche', icon: Compass },
    { id: 'confidence', label: 'Confidence Signals', icon: Shield },
    { id: 'enterprise', label: 'Strategic Enterprise', icon: Building2 },
    { id: 'flow', label: 'Daily Usage Flow', icon: ArrowRight },
    { id: 'screens', label: 'Screen Guide', icon: Monitor },
  ];

  return (
    <div className="space-y-5" data-testid="intelligence-guide">
      {/* Section nav */}
      <div className="flex flex-wrap gap-2">
        {sections.map(s => (
          <button key={s.id} onClick={() => setExpandedSection(s.id)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all flex items-center gap-1.5 ${
              expandedSection === s.id
                ? 'bg-[#0A1F44] text-white border-[#0A1F44]'
                : 'bg-white text-gray-600 border-gray-200 hover:border-[#D4AF37] hover:text-[#0A1F44]'
            }`} data-testid={`ig-nav-${s.id}`}>
            <s.icon className="h-3.5 w-3.5" /> {s.label}
          </button>
        ))}
      </div>

      {/* ── 1. TIER LOGIC ── */}
      {expandedSection === 'tiers' && (
        <div className="space-y-4" data-testid="ig-tiers">
          <div className="bg-gradient-to-r from-[#0A1F44] to-[#1a3a6e] p-5 rounded-xl text-white">
            <h2 className="text-base font-bold mb-1">Priority Tiers — What to Work and When</h2>
            <p className="text-xs text-blue-200">Every company in the system is classified into one of four tiers. This determines your outreach priority.</p>
          </div>
          {TIER_DEFINITIONS.map((t, i) => (
            <div key={i} className={`p-4 rounded-xl border-2 ${t.color}`} data-testid={`tier-card-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`px-3 py-1 text-xs font-bold rounded-full ${t.badge}`}>{t.tier}</span>
                <span className="text-[10px] font-medium uppercase tracking-wider opacity-60">
                  {i === 0 ? 'Work First' : i === 1 ? 'Second Pass' : i === 2 ? 'Selective' : 'Intentional'}
                </span>
              </div>
              <p className="text-xs leading-relaxed mb-2"><strong>What it means:</strong> {t.what}</p>
              <p className="text-xs leading-relaxed mb-2"><strong>What to do:</strong> {t.action}</p>
              <div className="flex flex-wrap gap-1.5">
                {t.signals.map((s, j) => (
                  <span key={j} className="px-2 py-0.5 text-[10px] bg-white/60 rounded-full font-medium">{s}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── 2. BROAD VS NICHE ── */}
      {expandedSection === 'views' && (
        <div className="space-y-4" data-testid="ig-views">
          <div className="bg-gradient-to-r from-[#0A1F44] to-[#1a3a6e] p-5 rounded-xl text-white">
            <h2 className="text-base font-bold mb-1">Broad Discovery vs Niche Focus</h2>
            <p className="text-xs text-blue-200">The system offers both wide-net discovery and deep-dive specialty views. They work together, not as alternatives.</p>
          </div>
          {BROAD_VS_NICHE.map((v, i) => (
            <div key={i} className="p-4 bg-white border border-gray-200 rounded-xl">
              <div className="flex items-center gap-3 mb-2">
                <div className="h-9 w-9 rounded-lg bg-[#0A1F44]/5 flex items-center justify-center">
                  <v.icon className="h-4.5 w-4.5 text-[#0A1F44]" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-[#0A1F44]">{v.title}</h3>
                  <p className="text-[10px] text-gray-500">{v.subtitle}</p>
                </div>
              </div>
              <p className="text-xs text-gray-600 leading-relaxed mb-2"><strong>When to use:</strong> {v.when}</p>
              <p className="text-[11px] text-[#D4AF37] font-medium">{v.tip}</p>
            </div>
          ))}
          <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-[11px] text-amber-800"><strong>Key point:</strong> The 4 specialty lanes are focused filters inside your qualified pipeline — not the only industries you can target. Every qualified company appears in the broad All Qualified view regardless of whether it matches a lane.</p>
          </div>
        </div>
      )}

      {/* ── 3. CONFIDENCE SIGNALS ── */}
      {expandedSection === 'confidence' && (
        <div className="space-y-4" data-testid="ig-confidence">
          <div className="bg-gradient-to-r from-[#0A1F44] to-[#1a3a6e] p-5 rounded-xl text-white">
            <h2 className="text-base font-bold mb-1">Confidence & Trust Signals</h2>
            <p className="text-xs text-blue-200">How much should you trust the system's classification of each company? Confidence tells you.</p>
          </div>
          {CONFIDENCE_SIGNALS.map((c, i) => (
            <div key={i} className={`p-4 rounded-xl border ${c.color}`}>
              <h3 className="text-sm font-bold mb-1">{c.level}</h3>
              <p className="text-xs leading-relaxed mb-2">{c.meaning}</p>
              {c.flags.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider opacity-60 mb-1">Common flags</p>
                  <div className="flex flex-wrap gap-1.5">
                    {c.flags.map((f, j) => (
                      <span key={j} className="px-2 py-0.5 text-[10px] bg-white/80 rounded-full font-medium">{f}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
          <div className="p-4 bg-white border border-gray-200 rounded-xl">
            <h4 className="text-xs font-bold text-[#0A1F44] mb-2">How to Treat Unclear Accounts</h4>
            <ul className="space-y-1.5">
              <li className="text-[11px] text-gray-600 flex items-start gap-1.5"><span className="text-[#D4AF37] font-bold mt-px">1.</span> Check the confidence flags on the company card — they tell you exactly what is missing</li>
              <li className="text-[11px] text-gray-600 flex items-start gap-1.5"><span className="text-[#D4AF37] font-bold mt-px">2.</span> Quick-verify on LinkedIn or the company website (30 seconds — check About page for headcount)</li>
              <li className="text-[11px] text-gray-600 flex items-start gap-1.5"><span className="text-[#D4AF37] font-bold mt-px">3.</span> If data is wrong, use Enrichment Overrides to correct it — the system reclassifies automatically</li>
              <li className="text-[11px] text-gray-600 flex items-start gap-1.5"><span className="text-[#D4AF37] font-bold mt-px">4.</span> If still unclear, skip and move to higher-confidence targets — plenty of Best Immediate to work first</li>
            </ul>
          </div>
        </div>
      )}

      {/* ── 4. STRATEGIC ENTERPRISE ── */}
      {expandedSection === 'enterprise' && (
        <div className="space-y-4" data-testid="ig-enterprise">
          <div className="bg-gradient-to-r from-indigo-900 to-indigo-700 p-5 rounded-xl text-white">
            <h2 className="text-base font-bold mb-1">Strategic Enterprise — Hidden, Not Junk</h2>
            <p className="text-xs text-indigo-200">Large companies are hidden by default but they are not excluded. They require a different approach.</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-xl">
              <h4 className="text-xs font-bold text-indigo-900 mb-2">What They Are</h4>
              <ul className="space-y-1.5">
                <li className="text-[11px] text-indigo-800 flex items-start gap-1.5"><CheckCircle className="h-3 w-3 mt-0.5 flex-shrink-0" /> Enterprise-class companies (1,000+ employees)</li>
                <li className="text-[11px] text-indigo-800 flex items-start gap-1.5"><CheckCircle className="h-3 w-3 mt-0.5 flex-shrink-0" /> Real companies with real hiring needs</li>
                <li className="text-[11px] text-indigo-800 flex items-start gap-1.5"><CheckCircle className="h-3 w-3 mt-0.5 flex-shrink-0" /> Worth tracking for long-term relationship building</li>
                <li className="text-[11px] text-indigo-800 flex items-start gap-1.5"><CheckCircle className="h-3 w-3 mt-0.5 flex-shrink-0" /> May have specific divisions or subsidiaries in your range</li>
              </ul>
            </div>
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
              <h4 className="text-xs font-bold text-red-900 mb-2">What They Are NOT</h4>
              <ul className="space-y-1.5">
                <li className="text-[11px] text-red-800 flex items-start gap-1.5"><AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" /> NOT excluded junk (staffing, retail, defense)</li>
                <li className="text-[11px] text-red-800 flex items-start gap-1.5"><AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" /> NOT low-quality targets</li>
                <li className="text-[11px] text-red-800 flex items-start gap-1.5"><AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" /> NOT filtered out from the data — just hidden from default views</li>
                <li className="text-[11px] text-red-800 flex items-start gap-1.5"><AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" /> NOT the same as government/defense exclusions</li>
              </ul>
            </div>
          </div>
          <div className="p-4 bg-white border border-gray-200 rounded-xl">
            <h4 className="text-xs font-bold text-[#0A1F44] mb-2">How to Use Strategic Enterprise</h4>
            <ul className="space-y-1.5">
              <li className="text-[11px] text-gray-600 flex items-start gap-1.5"><span className="text-[#D4AF37] font-bold mt-px">-</span> Toggle the Strategic Enterprise filter on intelligence pages to see them</li>
              <li className="text-[11px] text-gray-600 flex items-start gap-1.5"><span className="text-[#D4AF37] font-bold mt-px">-</span> Use when you deliberately want to pursue a large account</li>
              <li className="text-[11px] text-gray-600 flex items-start gap-1.5"><span className="text-[#D4AF37] font-bold mt-px">-</span> Different sales motion: target specific hiring managers or division leaders, not generic HR</li>
              <li className="text-[11px] text-gray-600 flex items-start gap-1.5"><span className="text-[#D4AF37] font-bold mt-px">-</span> Longer sales cycle — treat as a strategic investment, not a quick win</li>
            </ul>
          </div>
        </div>
      )}

      {/* ── 5. DAILY USAGE FLOW ── */}
      {expandedSection === 'flow' && (
        <div className="space-y-4" data-testid="ig-flow">
          <div className="bg-gradient-to-r from-[#0A1F44] to-[#1a3a6e] p-5 rounded-xl text-white">
            <h2 className="text-base font-bold mb-1">Daily Operating Flow</h2>
            <p className="text-xs text-blue-200">A simple priority sequence for working your intelligence surfaces each day.</p>
          </div>
          <div className="space-y-2">
            {DAILY_FLOW_STEPS.map((s, i) => (
              <div key={i} className="flex gap-3 p-3.5 bg-white border border-gray-200 rounded-xl" data-testid={`flow-step-${i}`}>
                <div className={`h-7 w-7 rounded-full ${s.color} text-white text-xs font-bold flex items-center justify-center flex-shrink-0`}>{s.step}</div>
                <div>
                  <h4 className="text-xs font-bold text-[#0A1F44]">{s.label}</h4>
                  <p className="text-[11px] text-gray-600 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="p-3.5 bg-emerald-50 border border-emerald-200 rounded-lg">
            <p className="text-[11px] text-emerald-800"><strong>Rule of thumb:</strong> Spend 70% of your time on Best Immediate, 20% on Broader Qualified, and 10% on selective Needs Enrichment / Strategic Enterprise. If Best Immediate is thin today, expand to Broader Qualified rather than reaching into low-confidence accounts.</p>
          </div>
        </div>
      )}

      {/* ── 6. SCREEN-SPECIFIC GUIDE ── */}
      {expandedSection === 'screens' && (
        <div className="space-y-4" data-testid="ig-screens">
          <div className="bg-gradient-to-r from-[#0A1F44] to-[#1a3a6e] p-5 rounded-xl text-white">
            <h2 className="text-base font-bold mb-1">Screen-by-Screen Quick Guide</h2>
            <p className="text-xs text-blue-200">What each intelligence surface is for and how to use it effectively.</p>
          </div>
          {SCREEN_GUIDES.map((sg, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-[#0A1F44]/5 flex items-center justify-center">
                  <sg.icon className="h-4 w-4 text-[#0A1F44]" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-[#0A1F44]">{sg.screen}</h3>
                  <p className="text-[10px] text-gray-400 font-medium">{sg.path}</p>
                </div>
              </div>
              <div className="px-4 py-3 space-y-2">
                <p className="text-xs text-gray-600"><strong className="text-[#0A1F44]">Purpose:</strong> {sg.purpose}</p>
                <p className="text-xs text-gray-600"><strong className="text-[#0A1F44]">How to use:</strong> {sg.howToUse}</p>
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {sg.keyFeatures.map((f, j) => (
                    <span key={j} className="px-2 py-0.5 text-[10px] bg-gray-100 text-gray-600 rounded-full font-medium">{f}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ───────────────────────────────────────────────────────────────────────
   DIGEST SETTINGS COMPONENT
   ─────────────────────────────────────────────────────────────────────── */

const DigestSettings = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newRecipient, setNewRecipient] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/digest/config`);
        setConfig(data);
      } catch { /* silent */ }
      setLoading(false);
    };
    load();
  }, []);

  const save = async (updated) => {
    setSaving(true);
    try {
      const data = await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/digest/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updated),
      });
      if (data.success) {
        setConfig(data.config);
        toast.success('Settings saved');
      }
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const addRecipient = () => {
    if (!newRecipient || !newRecipient.includes('@')) return;
    const updated = { ...config, recipients: [...(config?.recipients || []), newRecipient] };
    setNewRecipient('');
    save(updated);
  };
  const removeRecipient = (email) => {
    const updated = { ...config, recipients: (config?.recipients || []).filter(r => r !== email) };
    save(updated);
  };

  if (loading) return <div className="text-center py-8 text-gray-400 text-sm">Loading settings...</div>;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-5 max-w-2xl" data-testid="digest-settings">
      <h3 className="text-sm font-bold text-[#0A1F44]">Daily Digest Configuration</h3>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-[9px] text-gray-500 uppercase block mb-1">Enabled</label>
          <button onClick={() => save({ ...config, enabled: !config?.enabled })} disabled={saving}
            className={`px-4 py-2 text-xs font-semibold rounded-lg border transition-colors ${
              config?.enabled ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-50 text-gray-500 border-gray-200'
            }`} data-testid="digest-toggle">
            {config?.enabled ? 'Enabled' : 'Disabled'}
          </button>
        </div>
        <div>
          <label className="text-[9px] text-gray-500 uppercase block mb-1">Timezone</label>
          <select value={config?.timezone || 'America/New_York'}
            onChange={e => save({ ...config, timezone: e.target.value })}
            className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" data-testid="digest-timezone">
            {['America/New_York','America/Chicago','America/Denver','America/Los_Angeles','America/Phoenix','Europe/London','Europe/Berlin','Asia/Tokyo'].map(tz => (
              <option key={tz} value={tz}>{tz.replace('America/', '').replace('Europe/', '').replace('Asia/', '').replace('_', ' ')}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-[9px] text-gray-500 uppercase block mb-1">Send Time</label>
          <select value={config?.send_time || '07:30'}
            onChange={e => save({ ...config, send_time: e.target.value })}
            className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" data-testid="digest-send-time">
            {['06:00','06:30','07:00','07:30','08:00','08:30','09:00'].map(t => (
              <option key={t} value={t}>{t} AM</option>
            ))}
          </select>
        </div>
        <div className="flex items-end">
          <button onClick={async () => {
            try {
              const data = await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/recommendations/send-now`, { method: 'POST' });
              if (data.skipped) toast.info(`Digest skipped: ${data.reason}`);
              else if (data.error) toast.error(data.detail);
              else toast.success('Digest sent!');
            } catch { toast.error('Failed'); }
          }} className="px-4 py-2 text-xs font-medium bg-[#0A1F44] text-white rounded-lg hover:bg-[#0d2a5c]" data-testid="send-preview-btn">
            Send Now / Preview
          </button>
        </div>
      </div>

      <div>
        <label className="text-[9px] text-gray-500 uppercase block mb-1">Recipients</label>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {(config?.recipients || []).map(r => (
            <span key={r} className="flex items-center gap-1 px-2 py-1 bg-gray-100 text-xs rounded-full">
              {r}
              <button onClick={() => removeRecipient(r)} className="text-gray-400 hover:text-red-500 text-[10px] font-bold">x</button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input type="email" value={newRecipient} onChange={e => setNewRecipient(e.target.value)}
            placeholder="Add recipient email..." onKeyDown={e => e.key === 'Enter' && addRecipient()}
            className="flex-1 px-3 py-1.5 text-xs border border-gray-200 rounded-lg" data-testid="add-recipient-input" />
          <button onClick={addRecipient} disabled={saving}
            className="px-3 py-1.5 text-xs font-medium bg-gray-100 hover:bg-gray-200 rounded-lg" data-testid="add-recipient-btn">Add</button>
        </div>
      </div>
    </div>
  );
};

/* ───────────────────────────────────────────────────────────────────────
   MAIN COMPONENT
   ─────────────────────────────────────────────────────────────────────── */

const TrainingGuides = () => {
  const [activeModule, setActiveModule] = useState(null);
  const [activeSection, setActiveSection] = useState('intelligence');

  return (
    <IntelligenceLayout title="Training & Quick Start" subtitle="Your operating manual — from login to outreach in 30 minutes">
      <div className="space-y-6">
        {/* Section Tabs */}
        <div className="flex items-center gap-3 border-b border-gray-200 pb-1 overflow-x-auto">
          {[
            { id: 'intelligence', label: 'Intelligence Guide', icon: Layers },
            { id: 'quickstart', label: 'Quick Start (30 Min)', icon: Rocket },
            { id: 'modules', label: 'Training Modules', icon: BookOpen },
            { id: 'settings', label: 'Digest Settings', icon: Settings },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveSection(tab.id)}
              className={`px-4 py-2 text-xs font-semibold rounded-t-lg transition-colors flex items-center gap-1.5 ${
                activeSection === tab.id ? 'bg-white text-[#0A1F44] border border-b-0 border-gray-200' : 'text-gray-500 hover:text-[#0A1F44]'
              }`} data-testid={`training-tab-${tab.id}`}>
              <tab.icon className="h-3.5 w-3.5" /> {tab.label}
            </button>
          ))}
        </div>

        {/* ── INTELLIGENCE GUIDE ── */}
        {activeSection === 'intelligence' && (
          <IntelligenceGuide />
        )}

        {/* ── QUICK START GUIDE ── */}
        {activeSection === 'quickstart' && (
          <div className="space-y-6" data-testid="quickstart-section">
            <div className="bg-gradient-to-r from-[#0A1F44] to-[#1a3a6e] p-6 rounded-xl text-white">
              <h2 className="text-lg font-bold mb-1">Get Started in 30 Minutes</h2>
              <p className="text-xs text-blue-200">{"Login > Recommendations > Pick Role > Review Candidates > Generate Pitch > Send Email > Track > Follow Up. Follow these 7 steps daily."}</p>
            </div>

            {/* What Good Looks Like */}
            <div className="grid grid-cols-4 gap-3" data-testid="daily-benchmarks">
              {DAILY_BENCHMARKS.map((b, i) => (
                <div key={i} className="p-3.5 bg-white border border-[#D4AF37]/30 rounded-xl text-center">
                  <div className="text-xl font-black text-[#0A1F44]">{b.metric}</div>
                  <div className="text-[10px] text-gray-500 font-medium mt-0.5">{b.label}</div>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-gray-400 -mt-4 text-center font-medium tracking-wide uppercase">What a good day looks like</p>

            {/* Daily Workflow Steps */}
            <div className="space-y-3">
              {QUICK_START_STEPS.map((s) => (
                <div key={s.step} className="flex gap-4 p-4 bg-white border border-gray-200 rounded-xl hover:shadow-sm transition-shadow" data-testid={`qs-step-${s.step}`}>
                  <div className="flex flex-col items-center">
                    <div className="h-8 w-8 rounded-full bg-[#0A1F44] text-white text-sm font-bold flex items-center justify-center">{s.step}</div>
                    {s.step < 7 && <div className="w-px flex-1 bg-gray-200 mt-1" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <s.icon className="h-4 w-4 text-[#D4AF37]" />
                      <h3 className="text-sm font-bold text-[#0A1F44]">{s.title}</h3>
                    </div>
                    <p className="text-xs text-gray-600 leading-relaxed mb-1.5">{s.desc}</p>
                    {s.bullets && (
                      <div className="mb-2">
                        {s.bulletLabel && <p className="text-[11px] font-semibold text-[#0A1F44] mb-1">{s.bulletLabel}</p>}
                        <ul className="space-y-0.5">
                          {s.bullets.map((b, i) => (
                            <li key={i} className="text-[11px] text-gray-600 flex items-start gap-1.5">
                              <span className="text-[#D4AF37] font-bold mt-px">-</span> {b}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <div className="flex items-center gap-1 text-[10px] text-[#D4AF37] font-medium">
                      <ArrowRight className="h-3 w-3" /> {s.action}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* ACT vs SKIP */}
            <div className="grid grid-cols-2 gap-4" data-testid="act-vs-skip">
              {ACT_VS_SKIP.map(group => (
                <div key={group.type} className={`p-4 rounded-xl border ${
                  group.type === 'act' ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'
                }`}>
                  <h4 className={`text-xs font-black mb-2 uppercase tracking-wider ${
                    group.type === 'act' ? 'text-emerald-700' : 'text-red-700'
                  }`}>{group.label}</h4>
                  <ul className="space-y-1.5">
                    {group.items.map((item, i) => (
                      <li key={i} className={`text-[11px] flex items-start gap-1.5 ${
                        group.type === 'act' ? 'text-emerald-700' : 'text-red-700'
                      }`}>
                        {group.type === 'act'
                          ? <CheckCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                          : <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                        } {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Reference Cards */}
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-white border border-gray-200 rounded-xl">
                <h4 className="text-xs font-bold text-red-600 mb-2 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> What to IGNORE</h4>
                <ul className="space-y-1.5">
                  {WHAT_TO_IGNORE.map((item, i) => (
                    <li key={i} className="text-[11px] text-gray-600"><strong className="text-[#0A1F44]">{item.label}</strong> — {item.desc}</li>
                  ))}
                </ul>
              </div>
              <div className="p-4 bg-white border border-gray-200 rounded-xl">
                <h4 className="text-xs font-bold text-emerald-600 mb-2 flex items-center gap-1.5"><CheckCircle className="h-3.5 w-3.5" /> Good Pipeline Signs</h4>
                <ul className="space-y-1.5">
                  {GOOD_PIPELINE_SIGNS.map((item, i) => (
                    <li key={i} className="text-[11px] text-gray-600 flex items-start gap-1.5">
                      <CheckCircle className="h-3 w-3 text-emerald-400 mt-0.5 flex-shrink-0" /> {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="p-4 bg-white border border-gray-200 rounded-xl">
                <h4 className="text-xs font-bold text-blue-600 mb-2 flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Outreach Safety Rules</h4>
                <ul className="space-y-1.5">
                  {SAFE_SENDING.map((item, i) => (
                    <li key={i} className="text-[11px] text-gray-600 flex items-start gap-1.5">
                      <Clock className="h-3 w-3 text-blue-400 mt-0.5 flex-shrink-0" /> {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* ── TRAINING MODULES ── */}
        {activeSection === 'modules' && (
          <div className="space-y-3" data-testid="training-modules">
            <p className="text-xs text-gray-500 mb-2">9 modules covering every feature. Click to expand.</p>
            {MODULES.map(mod => (
              <div key={mod.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <button onClick={() => setActiveModule(activeModule === mod.id ? null : mod.id)}
                  className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
                  data-testid={`module-${mod.id}`}>
                  <div className="flex items-center gap-3">
                    <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${mod.color}`}>
                      <mod.icon className="h-4 w-4" />
                    </div>
                    <span className="text-sm font-bold text-[#0A1F44]">{mod.title}</span>
                    <span className="text-[10px] text-gray-400">{mod.sections.length} sections</span>
                  </div>
                  <ChevronRight className={`h-4 w-4 text-gray-400 transition-transform ${activeModule === mod.id ? 'rotate-90' : ''}`} />
                </button>
                {activeModule === mod.id && (
                  <div className="px-5 pb-4 space-y-3 border-t border-gray-100 pt-3">
                    {mod.sections.map((sec, i) => (
                      <div key={i}>
                        <h5 className="text-xs font-bold text-[#0A1F44] mb-1">{sec.heading}</h5>
                        <p className="text-xs text-gray-600 leading-relaxed">{sec.content}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ── DIGEST SETTINGS ── */}
        {activeSection === 'settings' && (
          <DigestSettings />
        )}
      </div>
    </IntelligenceLayout>
  );
};

export default TrainingGuides;
